"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import NoteViewDialog from "@/components/patient/notes/note-view-dialog";
import Badge from "@/components/ui/badge";
import { formatDate } from "@/lib/format-data";
import { getProviderNames } from "@/lib/get-provider-names";
import type { NoteListItem } from "@/types/notes";
import { SectionLoadError } from "./section-load-error";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
interface Props {
  patientId: string;
  /** The note open in the editor, which isn't "previous". */
  currentNoteId?: string;
}

/** Opens notes read-only in place, so the note being written stays open. */
export default function PreviousNotesSection(t0) {
  const $ = _c(27);
  const {
    patientId,
    currentNoteId
  } = t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = noteQueryOptions.sidebarPreview(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    isPending,
    isError,
    isRefetching,
    refetch
  } = useQuery(t1);
  const [viewingNote, setViewingNote] = useState(null);
  if (isPending) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <PatientSidebarSectionSkeleton />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (isError || !data) {
    let t2;
    if ($[3] !== refetch) {
      t2 = () => refetch();
      $[3] = refetch;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== isRefetching || $[6] !== t2) {
      t3 = <SectionLoadError label="previous notes" onRetry={t2} isRetrying={isRefetching} />;
      $[5] = isRefetching;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  const t2 = data.data;
  let t3;
  let t4;
  let t5;
  if ($[8] !== currentNoteId || $[9] !== data.data) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t6;
      if ($[13] !== currentNoteId) {
        t6 = note => note.id !== currentNoteId;
        $[13] = currentNoteId;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      const notes = t2.filter(t6);
      if (notes.length === 0) {
        let t7;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <p className="text-caption text-muted-foreground">No previous notes</p>;
          $[15] = t7;
        } else {
          t7 = $[15];
        }
        t5 = t7;
        break bb0;
      }
      t3 = "space-y-1";
      let t7;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = note_0 => <li key={note_0.id}><button type="button" className="w-full rounded-md px-2 py-1.5 text-left hover:bg-accent/50" onClick={() => setViewingNote(note_0)}><div className="flex items-center justify-between gap-2"><span className="truncate text-body font-medium text-foreground">{note_0.title || note_0.summary || "Note"}</span>{!(note_0.signedOffDate && note_0.signedOffProviderId) && <Badge size="xs" variant="warning">Unsigned</Badge>}</div><p className="truncate text-caption text-foreground-secondary">{[formatDate(note_0.visitDate ?? note_0.createdDate), note_0.providers?.length && getProviderNames(note_0.providers)].filter(Boolean).join(" \xB7 ")}</p></button></li>;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      t4 = notes.map(t7);
    }
    $[8] = currentNoteId;
    $[9] = data.data;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t4) {
    t6 = <ul className={t3}>{t4}</ul>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const t7 = !!viewingNote;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = open => !open && setViewingNote(null);
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== viewingNote) {
    t9 = <NoteViewDialog isOpen={t7} onOpenChange={t8} note={viewingNote} />;
    $[21] = t7;
    $[22] = viewingNote;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t6 || $[25] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[24] = t6;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}